import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Gem, Recycle, Check, Smartphone, Laptop } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import {
  SCREEN_PRICING,
  BATTERY_PRICING,
  SAMSUNG_PRICING,
  OTHER_REPAIRS,
  PC_SERVICES,
  PC_PARTS,
  PC_PARTS_NOTE,
  ENGAGEMENT,
} from "@/lib/site";
import { scrollToSection } from "@/lib/scroll";

const ICONS = { sparkles: Sparkles, gem: Gem, recycle: Recycle };
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const TABS = [
  { id: "ecrans-iphone", label: "Écrans iPhone" },
  { id: "batteries-iphone", label: "Batteries iPhone" },
  { id: "ecrans-samsung", label: "Écrans Samsung" },
  { id: "autres-smartphone", label: "Autres réparations" },
  { id: "pc", label: "PC portables" },
];

function TierCard({ t }) {
  const Icon = ICONS[t.icon] || Sparkles;
  return (
    <div
      className={`flex h-full flex-col rounded-3xl border p-6 transition-colors duration-300 sm:p-8 ${
        t.id.startsWith("premium")
          ? "border-neon/40 bg-gradient-to-b from-neon/10 via-ink to-ink hover:border-neon"
          : "border-line bg-ink hover:border-neon/40"
      }`}
    >
      <span className="inline-flex w-fit items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">
        <Icon size={13} />
        {t.badge}
      </span>
      <h3 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl">{t.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-400">{t.desc}</p>

      <div className="mt-6 flex items-baseline justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
        <span>{t.rowLabel}</span>
        <span>{t.priceLabel}</span>
      </div>
      <ul className="mt-2 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
        {t.rows.map(([label, price]) => (
          <li key={t.id + label} className="flex items-baseline justify-between gap-3 border-b border-line py-2.5">
            <span className="text-[13px] font-semibold text-slate-200">{label}</span>
            <span data-testid={`pricing-price-${t.id}-${slug(label)}`} className={`shrink-0 font-display text-lg font-extrabold ${price === "sur devis" || price.startsWith("à partir") || price.includes("+") ? "text-slate-400 !text-sm !font-bold self-center" : "text-neon"}`}>
              {price}
            </span>
          </li>
        ))}
      </ul>

      {t.note && <p className="mt-4 text-[11px] leading-relaxed text-slate-500">{t.note}</p>}

      {t.included && (
        <div className="mt-6 rounded-2xl border border-line bg-night/70 p-5">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Inclus</p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {t.included.map((item) => (
              <li key={item} className="flex items-center gap-2 text-[13px] text-slate-400">
                <Check size={14} className="shrink-0 text-neon" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {t.tagline && <p className="mt-5 text-sm italic leading-relaxed text-slate-400">{t.tagline}</p>}

      <button
        data-testid={`pricing-cta-${t.id}`}
        onClick={() => scrollToSection("#devis")}
        className="mt-6 flex items-center justify-center gap-2 self-stretch rounded-full bg-neon px-6 py-3.5 text-xs font-extrabold uppercase tracking-wider text-night chip-glow transition-transform duration-200 hover:scale-[1.02]"
      >
        Demander mon devis
        <ArrowRight size={15} />
      </button>
    </div>
  );
}

function PairTab({ tiers }) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {tiers.map((t) => (
        <TierCard key={t.id} t={t} />
      ))}
    </div>
  );
}

function OtherRepairsTab() {
  return (
    <div className="rounded-3xl border border-line bg-ink p-6 sm:p-8">
      <span className="inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">
        <Smartphone size={13} />
        Smartphone · toutes marques
      </span>
      <div className="mt-6 grid grid-cols-[1fr_auto_auto] items-baseline gap-x-6 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 sm:gap-x-10">
        <span>Intervention</span>
        <span className="text-right">Premium</span>
        <span className="text-right">Original / OEM</span>
      </div>
      <ul className="mt-2">
        {OTHER_REPAIRS.rows.map(([label, premium, oem]) => (
          <li key={label} className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-6 border-b border-line py-3 sm:gap-x-10">
            <span className="text-sm font-semibold text-slate-200">{label}</span>
            <span data-testid={`other-price-premium-${slug(label)}`} className="self-center text-right font-display text-lg font-extrabold text-neon">{premium}</span>
            <span data-testid={`other-price-oem-${slug(label)}`} className="self-center text-right font-display text-lg font-extrabold text-slate-400">{oem}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-[11px] text-slate-500">{OTHER_REPAIRS.note}</p>
    </div>
  );
}

function PcTab() {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-3xl border border-line bg-ink p-6 sm:p-8">
        <span className="inline-flex items-center gap-2 rounded-full border border-neon/30 bg-neon/10 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">
          <Laptop size={13} />
          {PC_SERVICES.title}
        </span>
        <ul className="mt-6">
          {PC_SERVICES.rows.map(([label, price]) => (
            <li key={label} className="flex items-baseline justify-between gap-4 border-b border-line py-2.5">
              <span className="text-[13px] font-semibold text-slate-200">{label}</span>
              <span data-testid={`pc-price-${slug(label)}`} className="shrink-0 font-display text-lg font-extrabold text-neon">{price}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[11px] leading-relaxed text-slate-500">{PC_SERVICES.note}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {PC_PARTS.map((part) => (
          <div key={part.title} className="flex flex-col rounded-3xl border border-line bg-ink p-6">
            <h4 className="font-display text-xl font-extrabold uppercase tracking-tight text-white">{part.title}</h4>
            <ul className="mt-4">
              {part.rows.map(([label, price]) => (
                <li key={label} className="flex items-baseline justify-between gap-3 border-b border-line py-2.5">
                  <span className="text-[13px] font-semibold text-slate-200">{label}</span>
                  <span data-testid={`pcpart-price-${slug(part.title)}-${slug(label)}`} className="shrink-0 font-display text-base font-extrabold text-neon">{price}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
        <p className="text-[11px] leading-relaxed text-slate-500 sm:col-span-2">{PC_PARTS_NOTE}</p>
      </div>
    </div>
  );
}

function EngagementBlock() {
  return (
    <Reveal delay={0.15}>
      <div className="mt-10 grid gap-8 rounded-3xl border border-neon/25 bg-gradient-to-br from-neon/10 via-transparent to-transparent p-7 lg:grid-cols-2 lg:p-9">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neon label-mono">Notre engagement</p>
          <h3 className="mt-3 font-display text-2xl font-extrabold uppercase tracking-tight text-white">Toutes les réparations comprennent</h3>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            {ENGAGEMENT.items.map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-slate-400">
                <Check size={15} className="shrink-0 text-neon" />
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="grid content-start gap-4">
          {ENGAGEMENT.levels.map((l) => {
            const Icon = ICONS[l.icon];
            return (
              <div key={l.name} className="flex items-start gap-4 rounded-2xl border border-line bg-night/70 p-5">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-neon/25 bg-neon/10 text-neon">
                  <Icon size={17} />
                </span>
                <div>
                  <p className="font-display text-lg font-extrabold uppercase tracking-tight text-white">{l.name}</p>
                  <p className="mt-0.5 text-sm text-slate-400">{l.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Reveal>
  );
}

export default function Pricing() {
  const [tab, setTab] = useState("ecrans-iphone");

  return (
    <section id="tarifs" className="py-24 lg:py-32">
      <div className="mx-auto w-full max-w-7xl px-5 lg:px-8">
        <SectionHead
          tag="Tarifs"
          title={<>Des prix clairs, <span className="text-neon">deux niveaux de qualité</span></>}
          sub="TTC, pose professionnelle incluse. Deux gammes de pièces pour s'adapter à votre budget — le devis est toujours confirmé avant toute intervention, jamais de surprise."
        />

        <Reveal delay={0.08}>
          <div className="mt-10 flex flex-wrap gap-2">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`pricing-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-wider transition-colors duration-200 ${
                  tab === t.id ? "bg-neon text-night chip-glow" : "border border-line bg-ink text-slate-400 hover:border-neon/50 hover:text-neon"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {tab === "ecrans-iphone" && <PairTab tiers={SCREEN_PRICING} />}
              {tab === "batteries-iphone" && <PairTab tiers={BATTERY_PRICING} />}
              {tab === "ecrans-samsung" && <PairTab tiers={SAMSUNG_PRICING} />}
              {tab === "autres-smartphone" && <OtherRepairsTab />}
              {tab === "pc" && <PcTab />}
            </motion.div>
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <button
            data-testid="pricing-unknown-model-cta"
            onClick={() => scrollToSection("#devis")}
            className="mt-8 flex w-full flex-col items-center gap-2 rounded-3xl border border-line bg-ink p-7 text-center transition-colors duration-300 hover:border-neon/40"
          >
            <span className="font-display text-2xl font-extrabold uppercase tracking-tight text-white">
              Votre modèle n'est pas dans la liste ?
            </span>
            <span className="text-sm text-slate-400">
              Envoyez-nous la marque + le modèle de votre appareil pour obtenir un tarif précis.
            </span>
            <span className="mt-2 flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-neon">
              Obtenir mon tarif
              <ArrowRight size={15} />
            </span>
          </button>
        </Reveal>

        <EngagementBlock />
      </div>
    </section>
  );
}
